import { APIClient } from "../helpers/api_helper";
import { ApiResponse, PaginatedApiResponse } from "../types/api";

const api = new APIClient();

export type CalendarExceptionType =
  | "holiday"
  | "closure"
  | "event"
  | "extended_hours";

export interface CalendarException {
  id: number;
  tenant_id?: number;
  unit_id: number | null;
  type: CalendarExceptionType;
  start_datetime: string;
  end_datetime: string;
  reason: string;
  source?: string;
  unit?: {
    id: number;
    name: string;
  } | null;
}

export interface CalendarExceptionPayload {
  unit_id?: number | null;
  type: CalendarExceptionType;
  start_datetime: string;
  end_datetime: string;
  reason: string;
}

export type CalendarExceptionSource =
  | "manual"
  | "br_national_import"
  | "br_state_import"
  | "br_city_import";

export interface CalendarExceptionListQuery {
  page?: number;
  per_page?: number;
  unit_id?: number;
  type?: CalendarExceptionType;
  source?: CalendarExceptionSource;
  year?: number;
  date?: string;
}

export interface BrazilianHolidayPreviewItem {
  code: string;
  name: string;
  date: string;
  optional: boolean;
  already_imported: boolean;
  has_manual_overlap: boolean;
}

export interface BrazilianHolidaysPreview {
  year: number;
  unit_id: number | null;
  include_optional_days: boolean;
  holidays: BrazilianHolidayPreviewItem[];
}

export interface BrazilianHolidaysImportResult {
  created: number;
  skipped: Array<{
    code: string;
    name: string;
    date: string;
    reason: string;
  }>;
  removed_replaced: number;
  exceptions: CalendarException[];
}

export interface BrazilianHolidaysRemoveResult {
  removed: number;
}

export interface BrazilianRegionalUfOption {
  uf: string;
  name: string;
  has_capital: boolean;
}

export interface BrazilianRegionalHolidayPreviewItem {
  code: string;
  name: string;
  date: string;
  optional: boolean;
  scope: "state" | "city";
  uf: string;
  already_imported: boolean;
  has_manual_overlap: boolean;
}

export interface BrazilianRegionalHolidaysPreview {
  year: number;
  uf: string;
  unit_id: number | null;
  include_optional_days: boolean;
  include_city: boolean;
  city: string | null;
  capital_match: { uf: string; city: string; matched: boolean } | null;
  holidays: BrazilianRegionalHolidayPreviewItem[];
}

export const listCalendarExceptions = (
  query: CalendarExceptionListQuery = {}
): Promise<PaginatedApiResponse<CalendarException>> =>
  api.get<PaginatedApiResponse<CalendarException>>("/calendar-exceptions", query);

export const previewBrazilianHolidays = (query: {
  year: number;
  unit_id?: number;
  include_optional_days?: boolean;
}): Promise<ApiResponse<BrazilianHolidaysPreview>> =>
  api.get<ApiResponse<BrazilianHolidaysPreview>>(
    "/calendar-exceptions/br-holidays/preview",
    query
  );

export const importBrazilianHolidays = (payload: {
  year: number;
  unit_id?: number | null;
  include_optional_days?: boolean;
  codes?: string[];
}): Promise<ApiResponse<BrazilianHolidaysImportResult>> =>
  api.create<ApiResponse<BrazilianHolidaysImportResult>>(
    "/calendar-exceptions/br-holidays/import",
    payload
  );

export const removeBrazilianHolidaysYear = (
  year: number,
  query: { unit_id?: number } = {}
): Promise<ApiResponse<BrazilianHolidaysRemoveResult>> => {
  const params = new URLSearchParams();
  if (query.unit_id !== undefined) {
    params.set("unit_id", String(query.unit_id));
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";

  return api.delete<ApiResponse<BrazilianHolidaysRemoveResult>>(
    `/calendar-exceptions/br-holidays/${year}${suffix}`
  );
};

export const listBrazilianRegionalHolidayCatalog = (): Promise<
  ApiResponse<{ ufs: BrazilianRegionalUfOption[] }>
> =>
  api.get<ApiResponse<{ ufs: BrazilianRegionalUfOption[] }>>(
    "/calendar-exceptions/br-regional-holidays/catalog"
  );

export const previewBrazilianRegionalHolidays = (query: {
  year: number;
  uf: string;
  unit_id?: number;
  include_optional_days?: boolean;
  include_city?: boolean;
  city?: string;
}): Promise<ApiResponse<BrazilianRegionalHolidaysPreview>> =>
  api.get<ApiResponse<BrazilianRegionalHolidaysPreview>>(
    "/calendar-exceptions/br-regional-holidays/preview",
    query
  );

export const importBrazilianRegionalHolidays = (payload: {
  year: number;
  uf: string;
  unit_id?: number | null;
  include_optional_days?: boolean;
  include_city?: boolean;
  city?: string;
  codes?: string[];
}): Promise<ApiResponse<BrazilianHolidaysImportResult>> =>
  api.create<ApiResponse<BrazilianHolidaysImportResult>>(
    "/calendar-exceptions/br-regional-holidays/import",
    payload
  );

export const removeBrazilianRegionalHolidaysYear = (
  year: number,
  query: { unit_id?: number; scope?: "all" | "state" | "city" } = {}
): Promise<ApiResponse<BrazilianHolidaysRemoveResult>> => {
  const params = new URLSearchParams();
  if (query.unit_id !== undefined) {
    params.set("unit_id", String(query.unit_id));
  }
  if (query.scope) {
    params.set("scope", query.scope);
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";

  return api.delete<ApiResponse<BrazilianHolidaysRemoveResult>>(
    `/calendar-exceptions/br-regional-holidays/${year}${suffix}`
  );
};

export const getCalendarException = (
  id: number
): Promise<ApiResponse<CalendarException>> =>
  api.get<ApiResponse<CalendarException>>(`/calendar-exceptions/${id}`);

export const createCalendarException = (
  payload: CalendarExceptionPayload
): Promise<ApiResponse<CalendarException>> =>
  api.create<ApiResponse<CalendarException>>("/calendar-exceptions", payload);

export const updateCalendarException = (
  id: number,
  payload: Partial<CalendarExceptionPayload>
): Promise<ApiResponse<CalendarException>> =>
  api.update<ApiResponse<CalendarException>>(`/calendar-exceptions/${id}`, payload);

export const deleteCalendarException = (
  id: number
): Promise<ApiResponse<unknown>> =>
  api.delete<ApiResponse<unknown>>(`/calendar-exceptions/${id}`);
